// In-memory mock backend. Sole purpose is to let the FE develop end-to-end while
// the real backend lands. Consumed only by campaignsApi.js — when real endpoints
// ship, delete this file and the single `import * as mockBackend` in campaignsApi.js.
//
// Shapes of returned values must match the documented REST responses in
// docs/crm-campaings/BACKEND-REQUIREMENTS.md — consumer code should not need
// to change when the backend swaps in.

import {
  MOCK_CAMPAIGNS,
  MOCK_AUDIENCE_USERS,
  MOCK_CAMPAIGN_STATS,
  MOCK_TOP_LINKS,
  MOCK_ACTIVITY_SERIES,
  CAMPAIGN_TEMPLATES,
} from './mockData';
import { CAMPAIGN_STATUS, CHANNEL, LIMITS } from './constants';

// Mutable in-memory store seeded from the mock data.
let campaigns = MOCK_CAMPAIGNS.map((c) => ({ ...c }));
const recipientsByCampaign = new Map();
const impressions = [];
const clicks = [];
const testSendsByCampaign = new Map();

const nowIso = () => new Date().toISOString();

const ensureRecipients = (campaign) => {
  if (recipientsByCampaign.has(campaign.id)) return recipientsByCampaign.get(campaign.id);
  const total = campaign.metrics?.recipients ?? 0;
  const rows = [];
  for (let i = 0; i < Math.min(total, 200); i++) {
    const base = MOCK_AUDIENCE_USERS[i % MOCK_AUDIENCE_USERS.length];
    rows.push({
      id: `rec-${campaign.id}-${i}`,
      campaignId: campaign.id,
      userId: base.id,
      email: base.email,
      name: base.name,
      status: i < (campaign.metrics?.delivered ?? 0) ? 'delivered' : 'bounced',
      openedAt: i < (campaign.metrics?.opened ?? 0) ? nowIso() : null,
      clickedAt: i < (campaign.metrics?.clicked ?? 0) ? nowIso() : null,
      convertedAt: i < (campaign.metrics?.converted ?? 0) ? nowIso() : null,
      conversionValue: i < (campaign.metrics?.converted ?? 0) ? 80 : 0,
    });
  }
  recipientsByCampaign.set(campaign.id, rows);
  return rows;
};

// --- Queries -----------------------------------------------------------------

export const listCampaigns = ({ filters = {}, page = 1, pageSize = 20, includeDeleted = false } = {}) => {
  const search = (filters.search ?? '').trim().toLowerCase();

  let items = campaigns.filter((c) => {
    if (!includeDeleted && c.deletedAt) return false;
    if (filters.status && c.status !== filters.status) return false;
    if (filters.channel && !c.channels.includes(filters.channel)) return false;
    if (filters.offer && c.offer !== filters.offer) return false;
    if (search && !c.name.toLowerCase().includes(search)) return false;
    if (filters.dateRange?.from || filters.dateRange?.to) {
      const created = new Date(c.createdAt).getTime();
      if (filters.dateRange?.from && created < new Date(filters.dateRange.from).getTime()) return false;
      if (filters.dateRange?.to && created > new Date(filters.dateRange.to).getTime()) return false;
    }
    return true;
  });

  const total = items.length;
  const start = (page - 1) * pageSize;
  items = items.slice(start, start + pageSize);

  return { items, total, page, pageSize };
};

export const getCampaign = (id) => campaigns.find((c) => c.id === id) ?? null;

export const getStats = () => ({ ...MOCK_CAMPAIGN_STATS });

export const previewAudience = ({ filters = [], excludeUserIds = [] } = {}) => {
  let users = MOCK_AUDIENCE_USERS.slice();

  for (const f of filters) {
    if (!f?.type) continue;
    if (f.type === 'emailDomain' && Array.isArray(f.value) && f.value.length) {
      users = users.filter((u) => f.value.some((d) => u.email.endsWith(`@${d.replace(/^@/, '')}`)));
    }
    if (f.type === 'role' && Array.isArray(f.value) && f.value.length) {
      users = users.filter((u) => f.value.includes(u.role));
    }
  }

  if (excludeUserIds?.length) {
    users = users.filter((u) => !excludeUserIds.includes(u.id));
  }

  // Simulate a larger dataset behind the mock sample.
  const matchCount = users.length * 249;
  const suppressedForConsent = Math.floor(matchCount * 0.065);
  const count = matchCount - suppressedForConsent;

  return {
    count,
    matchCount,
    suppressedForConsent,
    sampleUsers: users.slice(0, 5),
  };
};

export const getRecipients = ({ id, page = 1, pageSize = 25, status }) => {
  const campaign = getCampaign(id);
  if (!campaign) return { items: [], total: 0, page, pageSize };

  // Anonymized campaigns expose only aggregate counts — recipient PII is gone.
  if (campaign.anonymizedAt) {
    const m = campaign.metrics ?? {};
    return {
      anonymized: true,
      anonymizedAt: campaign.anonymizedAt,
      aggregate: {
        recipients: m.recipients ?? 0,
        delivered: m.delivered ?? 0,
        opened: m.opened ?? 0,
        clicked: m.clicked ?? 0,
        converted: m.converted ?? 0,
        bounces: m.bounces ?? 0,
        unsubscribes: m.unsubscribes ?? 0,
      },
    };
  }

  let rows = ensureRecipients(campaign);
  if (status) rows = rows.filter((r) => r.status === status);

  const total = rows.length;
  const start = (page - 1) * pageSize;
  return { items: rows.slice(start, start + pageSize), total, page, pageSize, anonymized: false };
};

export const getActiveUpsells = () => {
  const active = campaigns.filter(
    (c) =>
      c.channels.includes(CHANNEL.NETWORK_UPSELL) &&
      c.status === CAMPAIGN_STATUS.ACTIVE &&
      !c.deletedAt &&
      !c.anonymizedAt,
  );
  return active.map((c) => ({
    placementId: `pl-${c.id}`,
    campaignId: c.id,
    featuredCourseIds: c.content?.networkUpsell?.featuredCourseIds ?? [],
    offer: c.offer,
    discount: c.discount ?? null,
    invitation: c.invitation ?? null,
    activeFrom: c.schedule?.networkUpsell?.activeFrom ?? null,
    activeUntil: c.schedule?.networkUpsell?.activeUntil ?? null,
  }));
};

// --- Mutations ---------------------------------------------------------------

const blankMetrics = () => ({
  recipients: 0, delivered: 0, opened: 0, clicked: 0, converted: 0, revenue: 0,
  openRate: 0, clickRate: 0, ctr: 0, impressions: 0, uniqueImpressions: 0,
  bounces: 0, unsubscribes: 0,
});

export const createCampaign = (data) => {
  const now = nowIso();
  const created = {
    ...data,
    id: `camp-${Date.now()}`,
    status: data.status ?? CAMPAIGN_STATUS.DRAFT,
    metrics: blankMetrics(),
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    anonymizedAt: null,
  };
  campaigns = [created, ...campaigns];
  return created;
};

export const updateCampaign = (id, patch) => {
  const idx = campaigns.findIndex((c) => c.id === id);
  if (idx < 0) return null;
  const next = { ...campaigns[idx], ...patch, updatedAt: nowIso() };
  campaigns[idx] = next;
  return next;
};

export const updateStatus = (id, status) => {
  const idx = campaigns.findIndex((c) => c.id === id);
  if (idx < 0) return null;
  campaigns[idx] = { ...campaigns[idx], status, updatedAt: nowIso() };
  return campaigns[idx];
};

export const sendCampaign = (id, sendAt) => {
  const idx = campaigns.findIndex((c) => c.id === id);
  if (idx < 0) return null;
  const status = sendAt ? CAMPAIGN_STATUS.SCHEDULED : CAMPAIGN_STATUS.ACTIVE;
  campaigns[idx] = {
    ...campaigns[idx],
    status,
    schedule: {
      ...campaigns[idx].schedule,
      email: { ...(campaigns[idx].schedule?.email ?? {}), sendAt: sendAt ?? nowIso(), mode: sendAt ? 'scheduled' : 'now' },
    },
    updatedAt: nowIso(),
  };
  return campaigns[idx];
};

export const testSend = (id, recipients = []) => {
  const current = testSendsByCampaign.get(id) ?? [];
  const remaining = LIMITS.TEST_SEND_PER_HOUR - current.length;
  if (remaining <= 0) {
    return { ok: false, reason: 'rate_limited' };
  }
  const sent = recipients.slice(0, LIMITS.TEST_SEND_RECIPIENTS_MAX);
  testSendsByCampaign.set(id, [...current, { recipients: sent, sentAt: nowIso() }]);
  return { ok: true, sent, remaining: remaining - 1 };
};

export const duplicateCampaign = (id) => {
  const original = getCampaign(id);
  if (!original) return null;
  const now = nowIso();
  const copy = {
    ...original,
    id: `camp-${Date.now()}`,
    name: `${original.name} (Copy)`,
    status: CAMPAIGN_STATUS.DRAFT,
    metrics: blankMetrics(),
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    anonymizedAt: null,
  };
  campaigns = [copy, ...campaigns];
  return copy;
};

export const softDeleteCampaign = (id) => {
  const idx = campaigns.findIndex((c) => c.id === id);
  if (idx < 0) return;
  campaigns[idx] = { ...campaigns[idx], deletedAt: nowIso(), updatedAt: nowIso() };
};

export const restoreCampaign = (id) => {
  const idx = campaigns.findIndex((c) => c.id === id);
  if (idx < 0) return null;
  campaigns[idx] = { ...campaigns[idx], deletedAt: null, updatedAt: nowIso() };
  return campaigns[idx];
};

export const hardDeleteCampaign = (id) => {
  campaigns = campaigns.filter((c) => c.id !== id);
};

export const exportAudience = ({ filters, format = 'csv' }) => {
  const preview = previewAudience({ filters });
  return {
    downloadUrl: '#mock-export-url',
    filename: `audience-export-${Date.now()}.${format}`,
    count: preview.count,
  };
};

export const trackUpsellImpression = (data) => {
  impressions.push({ ...data, ts: nowIso() });
};

export const trackUpsellClick = (data) => {
  clicks.push({ ...data, ts: nowIso() });
};

// Exposed for the detail page's additional panels (activity chart, top links, templates).
export const getActivitySeries = () => MOCK_ACTIVITY_SERIES.slice();
export const getTopLinks = () => MOCK_TOP_LINKS.slice();
export const getTemplates = () => CAMPAIGN_TEMPLATES.slice();
